import {
    blockTypeHasMedia,
    getArticleRowLayoutColumnCount,
    inferArticleRowLayoutFromColumnCount,
    normalizeArticleColumnCount,
    normalizeArticleRowLayoutKey,
} from '@containers/Art/Articles/Articles.constants';
import {
    makeArticleMedia,
    makePersistedArticleMedia,
} from '@containers/Art/Articles/ArticlesRepository';
import {
    getArtMediaDocumentId,
    getArtStoredMediaType,
    getArtStoredMediaUrl,
} from '@containers/Art/ArtDocuments';

export const normalizeTitle = (value) => {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ');
};

export const sortByPosition = (items = []) => {
    return [...items].sort((firstItem, secondItem) => firstItem.Position - secondItem.Position);
};

export const refreshRowsPositions = (rows = []) => {
    return rows.map((row, rowIndex) => ({
        ...row,
        Columns: sortByPosition(row.Columns).map((column, columnIndex) => ({
            ...column,
            Blocks: sortByPosition(column.Blocks).map((block, blockIndex) => ({
                ...block,
                Position: blockIndex + 1,
            })),
            Position: columnIndex + 1,
        })),
        Position: rowIndex + 1,
    }));
};

export const moveItem = (items, sourceIndex, destinationIndex) => {
    if (destinationIndex < 0 || destinationIndex >= items.length) return items;

    const nextItems = [...items];
    const [movedItem] = nextItems.splice(sourceIndex, 1);
    nextItems.splice(destinationIndex, 0, movedItem);
    return nextItems;
};

const ARTICLE_BLOCK_DROPPABLE_PREFIX = 'article-block-column';

export const getArticleBlockDroppableId = (rowId, columnId) => {
    return `${ARTICLE_BLOCK_DROPPABLE_PREFIX}:${rowId}:${columnId}`;
};

export const parseArticleBlockDroppableId = (droppableId = '') => {
    const [prefix, rowId, columnId] = String(droppableId).split(':');

    if (prefix !== ARTICLE_BLOCK_DROPPABLE_PREFIX || !rowId || !columnId) {
        return null;
    }

    return { columnId, rowId };
};

const isSameArticleElement = (firstId, secondId) => String(firstId) === String(secondId);

const findColumnByLocation = (rows, location) => {
    const row = rows.find((item) => isSameArticleElement(item.ID, location.rowId));
    if (!row) return null;

    return sortByPosition(row.Columns).find((column) => isSameArticleElement(column.ID, location.columnId)) || null;
};

const refreshBlocksPositions = (blocks) =>
    blocks.map((block, blockIndex) => ({
        ...block,
        Position: blockIndex + 1,
    }));

export const moveArticleBlock = (rows, sourceLocation, destinationLocation, sourceIndex, destinationIndex) => {
    const sourceColumn = findColumnByLocation(rows, sourceLocation);
    const destinationColumn = findColumnByLocation(rows, destinationLocation);

    if (!sourceColumn || !destinationColumn) return rows;

    const sourceBlocks = sortByPosition(sourceColumn.Blocks);
    const movedBlock = sourceBlocks[sourceIndex];
    if (!movedBlock) return rows;

    return rows.map((row) => ({
        ...row,
        Columns: sortByPosition(row.Columns).map((column) => {
            const isSourceColumn =
                isSameArticleElement(row.ID, sourceLocation.rowId) &&
                isSameArticleElement(column.ID, sourceLocation.columnId);
            const isDestinationColumn =
                isSameArticleElement(row.ID, destinationLocation.rowId) &&
                isSameArticleElement(column.ID, destinationLocation.columnId);

            if (!isSourceColumn && !isDestinationColumn) return column;

            const nextBlocks = sortByPosition(column.Blocks).filter(
                (block) => !isSourceColumn || !isSameArticleElement(block.ID, movedBlock.ID)
            );

            if (isDestinationColumn) {
                const safeDestinationIndex = Math.max(0, Math.min(destinationIndex, nextBlocks.length));
                nextBlocks.splice(safeDestinationIndex, 0, movedBlock);
            }

            return {
                ...column,
                Blocks: refreshBlocksPositions(nextBlocks),
            };
        }),
    }));
};

export const getInitialRows = (article) => {
    if (!article) return [];
    return (article.Rows || []).map((row) => {
        const countGuess = normalizeArticleColumnCount(row.ColumnCount || row.Columns?.length || 1);
        const layout = normalizeArticleRowLayoutKey(
            row.Layout,
            inferArticleRowLayoutFromColumnCount(countGuess)
        );
        const columnCount = getArticleRowLayoutColumnCount(layout);
        return {
            ...row,
            ColumnCount: columnCount,
            Layout: layout,
        };
    });
};

export const getMediaDisplayName = (media) => {
    const documentId = getArtMediaDocumentId(media);
    if (documentId) return `Document #${documentId}`;
    if (media.EmbedInput) return media.EmbedInput;
    return 'Article media';
};

/**
 * Validates media blocks before save.
 * @throws {Error} When validation fails
 */
export const prepareArticleRowsForSave = (sortedRows) => {
    return sortedRows.map((row) => ({
        ...row,
        Columns: row.Columns.map((column) => ({
            ...column,
            Blocks: column.Blocks.map((block) => {
                if (!blockTypeHasMedia(block.Type)) return block;

                const media = block.Media || makeArticleMedia();
                if (!getArtMediaDocumentId(media)) {
                    throw new Error('Please upload media or remove empty media/file blocks');
                }
                if (!getArtStoredMediaType(media) || !getArtStoredMediaUrl(media)) {
                    throw new Error('Please select article media again before saving');
                }

                return {
                    ...block,
                    Media: makePersistedArticleMedia(media),
                };
            }),
        })),
    }));
};
